// Standard Report Center (Increments R2+R3, docs/reporting-foundation.md) — a
// real, role-aware template gallery over the frontend registry. Every card is
// backed by a live endpoint or deep-links into an existing export flow;
// visibility is canonical-RBAC (ANY-of per template). R3 adds "My reports":
// saved definitions from the payroll-service definitions API (hidden entirely
// for plain employees, whose calls 403).
import { formatDate } from '@/lib/orgFormat';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '@/lib/apiError';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/shared/components/ui/card';
import { Button } from '@/features/shared/components/ui/button';
import { Badge } from '@/features/shared/components/ui/badge';
import { Input } from '@/features/shared/components/ui/input';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/features/shared/components/ui/dropdown-menu';
import {
  FileBarChart2,
  Search,
  Star,
  Clock,
  ShieldAlert,
  ExternalLink,
  Play,
  Plus,
  Bookmark,
  MoreVertical,
  Copy,
  Archive,
} from 'lucide-react';
import { useRbacRoles, useHasPermission } from '@/rbac/useRbac';
import { useConfirm } from '@/features/shared/components/confirm/ConfirmProvider';
import {
  CATEGORY_LABELS,
  CATEGORY_ORDER,
  getTemplate,
  templatesInCategory,
  visibleTemplates,
  type ReportTemplate,
  type TemplateKind,
} from '@/features/reports/registry';
import { getFavorites, getRecents, recordRecent, toggleFavorite } from '@/features/reports/storage';
import {
  archiveSavedReport,
  duplicateSavedReport,
  listSavedReports,
  DOMAIN_LABELS,
  type SavedReport,
} from '@/services/savedReportService';

const KIND_STYLES: Record<TemplateKind, string> = {
  live: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  snapshot: 'bg-sky-50 text-sky-700 border-sky-200',
  passthrough: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const ReportsPage: React.FC = () => {
  const { t } = useTranslation('reports');
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const roles = useRbacRoles();
  const canCreate = useHasPermission('reports:create');

  const base = pathname.replace(/\/$/, ''); // .../reports
  const tenantRoot = base.replace(/\/reports$/, '');

  const confirm = useConfirm();

  const [query, setQuery] = useState('');
  const [favorites, setFavorites] = useState<string[]>(() => getFavorites());
  const [recents, setRecents] = useState(() => getRecents());

  // Saved definitions ("My reports"). null = not loaded / not allowed — the
  // section hides entirely (plain employees 403 on every definitions call).
  const [savedReports, setSavedReports] = useState<SavedReport[] | null>(null);

  useEffect(() => {
    let active = true;
    listSavedReports('active')
      .then((defs) => active && setSavedReports(defs))
      .catch(() => active && setSavedReports(null));
    return () => {
      active = false;
    };
  }, []);

  const visible = useMemo(() => visibleTemplates(roles), [roles]);

  const kindLabel = (kind: TemplateKind): string =>
    kind === 'live'
      ? t('kind.live', 'Live data')
      : kind === 'snapshot'
        ? t('kind.snapshot', 'Payroll snapshot')
        : t('kind.passthrough', 'Export');

  const tplName = (tpl: ReportTemplate) => t(`templates.${tpl.id}.name`, tpl.name);
  const tplDescription = (tpl: ReportTemplate) => t(`templates.${tpl.id}.description`, tpl.description);

  const matchesQuery = (tpl: ReportTemplate): boolean => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      tplName(tpl).toLowerCase().includes(q) ||
      tplDescription(tpl).toLowerCase().includes(q) ||
      tpl.name.toLowerCase().includes(q) ||
      tpl.description.toLowerCase().includes(q)
    );
  };

  const filtered = useMemo(
    () => visible.filter(matchesQuery),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [visible, query],
  );

  const runTemplate = (tpl: ReportTemplate) => {
    if (tpl.kind === 'passthrough') {
      setRecents(recordRecent(tpl.id));
      navigate(`${tenantRoot}/${tpl.route ?? ''}`);
      return;
    }
    // Live/snapshot: the viewer records the run once it executes.
    navigate(`${base}/${tpl.id}`);
  };

  const onToggleFavorite = (id: string) => setFavorites(toggleFavorite(id));

  // ---- Saved definitions ("My reports") ---------------------------------------

  const openSaved = (def: SavedReport) => navigate(`${base}/saved/${def.id}`);

  const onDuplicateSaved = async (def: SavedReport) => {
    try {
      const copy = await duplicateSavedReport(def.id);
      setSavedReports((cur) => (cur ? [copy, ...cur] : [copy]));
      toast.success(t('saved.duplicated', 'Report duplicated.'));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('saved.duplicateFailed', 'Could not duplicate this report.')));
    }
  };

  const onArchiveSaved = async (def: SavedReport) => {
    const ok = await confirm({
      title: t('saved.archiveTitle', 'Archive saved report?'),
      message: t('saved.archiveMessage', '"{{name}}" will be removed from My reports. The underlying template stays available.', {
        name: def.name,
      }),
      confirmLabel: t('saved.archive', 'Archive'),
      danger: true,
    });
    if (!ok) return;
    try {
      await archiveSavedReport(def.id);
      setSavedReports((cur) => (cur ? cur.filter((d) => d.id !== def.id) : cur));
      toast.success(t('saved.archived', 'Saved report archived.'));
    } catch (e: any) {
      toast.error(getApiErrorMessage(e, t('saved.archiveFailed', 'Could not archive this report.')));
    }
  };

  const renderSavedCard = (def: SavedReport) => {
    const originTpl = def.template_id ? getTemplate(def.template_id) : undefined;
    return (
      <Card
        key={def.id}
        className="flex flex-col cursor-pointer transition-colors hover:border-slate-300"
        onClick={() => openSaved(def)}
      >
        <CardHeader className="pb-2">
          <div className="flex items-start justify-between gap-2">
            <CardTitle className="text-base leading-snug">{def.name}</CardTitle>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  aria-label={t('saved.actions', 'Saved report actions')}
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 text-muted-foreground hover:text-foreground transition-colors"
                >
                  <MoreVertical className="w-4 h-4" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" onClick={(e) => e.stopPropagation()}>
                <DropdownMenuItem onClick={() => onDuplicateSaved(def)}>
                  <Copy className="w-4 h-4 mr-2" />
                  {t('saved.duplicate', 'Duplicate')}
                </DropdownMenuItem>
                <DropdownMenuItem className="text-red-600 focus:text-red-600" onClick={() => onArchiveSaved(def)}>
                  <Archive className="w-4 h-4 mr-2" />
                  {t('saved.archive', 'Archive')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-1">
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-[11px] font-medium text-slate-600">
              {t(`domains.${def.domain}`, DOMAIN_LABELS[def.domain] ?? def.domain)}
            </span>
            {def.visibility === 'tenant' && (
              <Badge className="bg-indigo-50 text-indigo-700 border border-indigo-200 hover:bg-indigo-50 text-[11px]">
                {t('saved.companyWide', 'Company-wide')}
              </Badge>
            )}
            {(def.visibility === 'role' || def.visibility === 'users') && (
              <Badge className="bg-sky-50 text-sky-700 border border-sky-200 hover:bg-sky-50 text-[11px]">
                {t('saved.shared', 'Shared')}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent className="flex flex-col flex-1 pt-1">
          <p className="text-sm text-muted-foreground flex-1">
            {originTpl
              ? t('saved.fromTemplate', 'From “{{template}}”', { template: tplName(originTpl) })
              : !def.template_id
                ? def.description || t('saved.builderReport', 'Custom report built in the report builder')
                : t('saved.templateGone', 'Original template no longer available')}
          </p>
          <p className="text-xs text-muted-foreground mt-3">
            {def.last_run_at
              ? t('saved.lastRun', 'Last run {{when}}', { when: new Date(def.last_run_at).toLocaleString() })
              : t('saved.neverRun', 'Never run')}
          </p>
        </CardContent>
      </Card>
    );
  };

  const favoriteTemplates = favorites
    .map((id) => visible.find((tpl) => tpl.id === id))
    .filter((tpl): tpl is ReportTemplate => !!tpl);

  const recentEntries = recents
    .map((r) => ({ ...r, tpl: getTemplate(r.id) }))
    .filter((r) => r.tpl && visible.includes(r.tpl));

  const renderCard = (tpl: ReportTemplate) => (
    <Card key={tpl.id} className="flex flex-col">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base leading-snug">{tplName(tpl)}</CardTitle>
          <button
            type="button"
            aria-label={t('favorite', 'Favorite')}
            onClick={() => onToggleFavorite(tpl.id)}
            className="shrink-0 text-muted-foreground hover:text-amber-500 transition-colors"
          >
            <Star
              className={`w-4 h-4 ${favorites.includes(tpl.id) ? 'fill-amber-400 text-amber-400' : ''}`}
            />
          </button>
        </div>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium ${KIND_STYLES[tpl.kind]}`}>
            {kindLabel(tpl.kind)}
          </span>
          {tpl.sensitive && (
            <Badge className="bg-warning-soft text-warning-strong border border-transparent hover:bg-warning-soft text-[11px]">
              <ShieldAlert className="w-3 h-3 mr-1" />
              {t('confidential', 'Confidential')}
            </Badge>
          )}
        </div>
      </CardHeader>
      <CardContent className="flex flex-col flex-1 pt-1">
        <p className="text-sm text-muted-foreground flex-1">{tplDescription(tpl)}</p>
        <div className="mt-4">
          <Button size="sm" variant={tpl.kind === 'passthrough' ? 'outline' : 'default'} onClick={() => runTemplate(tpl)}>
            {tpl.kind === 'passthrough' ? (
              <>
                <ExternalLink className="w-4 h-4 mr-2" />
                {t('open', 'Open')}
              </>
            ) : (
              <>
                <Play className="w-4 h-4 mr-2" />
                {t('run', 'Run')}
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );

  if (visible.length === 0) {
    return (
      <div className="space-y-6">
        <div className="border-b border-slate-200 pb-6">
          <h1 className="text-3xl font-bold text-foreground">{t('title', 'Reports')}</h1>
          <p className="text-slate-600 mt-1">
            {t('subtitle', 'Standard reports for your organization, filtered to your role.')}
          </p>
        </div>
        <div className="text-center py-16">
          <FileBarChart2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-foreground mb-2">
            {t('empty.title', 'No reports available for your role')}
          </h3>
          <p className="text-slate-600 max-w-md mx-auto">
            {t(
              'empty.body',
              'Reports appear here based on your permissions. If you believe you should have access, ask your administrator.',
            )}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="border-b border-slate-200 pb-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('title', 'Reports')}</h1>
            <p className="text-slate-600 mt-1">
              {t('subtitle', 'Standard reports for your organization, filtered to your role.')}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div className="relative w-full sm:w-72">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('searchPlaceholder', 'Search reports…')}
                className="pl-9"
              />
            </div>
            {canCreate && (
              <Button onClick={() => navigate(`${base}/builder`)}>
                <Plus className="w-4 h-4 mr-2" />
                {t('builder.newReport', 'New report')}
              </Button>
            )}
          </div>
        </div>
      </div>

      {recentEntries.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Clock className="w-4 h-4" /> {t('recentlyRun', 'Recently run')}
          </h2>
          <div className="flex flex-wrap gap-2">
            {recentEntries.map((r) => (
              <button
                key={r.id}
                type="button"
                onClick={() => runTemplate(r.tpl!)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-card px-3 py-1.5 text-sm hover:border-slate-300 hover:bg-slate-50 transition-colors"
              >
                <span className="font-medium text-foreground">{tplName(r.tpl!)}</span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(r.at)} {new Date(r.at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}

      {savedReports !== null && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Bookmark className="w-4 h-4" /> {t('saved.myReports', 'My reports')}
          </h2>
          {savedReports.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('saved.empty', 'Save any report from its viewer to see it here.')}
            </p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {savedReports.map(renderSavedCard)}
            </div>
          )}
        </div>
      )}

      {favoriteTemplates.length > 0 && (
        <div>
          <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2 flex items-center gap-1.5">
            <Star className="w-4 h-4" /> {t('favorites', 'Favorites')}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {favoriteTemplates.map(renderCard)}
          </div>
        </div>
      )}

      {CATEGORY_ORDER.map((category) => {
        const templates = templatesInCategory(filtered, category);
        if (templates.length === 0) return null;
        return (
          <div key={category}>
            <h2 className="text-sm font-semibold text-slate-500 uppercase tracking-wide mb-2">
              {t(`categories.${category}`, CATEGORY_LABELS[category])}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {templates.map(renderCard)}
            </div>
          </div>
        );
      })}

      {filtered.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          {t('noMatches', 'No reports match your search.')}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
